import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Edit, Trash2, User, Calendar, MapPin,
  FileText, Receipt, ClipboardList, Plus, X, Clock,
  ExternalLink, Package, Flag, Zap, Euro, TrendingUp, TrendingDown, AlertTriangle, Truck,
  Share2, Copy, Link2, Unlink
} from '../icons/wp-icons';
import Layout from '../components/Layout/Layout';
import { getProjektById, updateProjekt, deleteProjekt, getProjektStatistiken, getErweiterteStatistiken, addTerminToProjekt, generatePublicToken, revokePublicToken } from '../api/projektApi';
import { getAllAnfragen } from '../api/anfrageApi';
import { addAnfrageToProjekt, removeAnfrageFromProjekt } from '../api/projektApi';
import { createTermin } from '../api/terminApi';
import TerminForm from '../components/Termin/TerminForm';
import toast from 'react-hot-toast';

// Phase 2 Komponenten
import ProjektMaterialien from '../components/Projekt/ProjektMaterialien';
import ProjektMeilensteine from '../components/Projekt/ProjektMeilensteine';
import ProjektZeiterfassung from '../components/Projekt/ProjektZeiterfassung';
import ProjektGesamtrechnung from '../components/Projekt/ProjektGesamtrechnung';
import ProjektDateien from '../components/Projekt/ProjektDateien';
import ProjektTimeline from '../components/Projekt/ProjektTimeline';
import ProjektNotizen from '../components/Projekt/ProjektNotizen';
import ProjektBestellungen from '../components/Projekt/ProjektBestellungen';

const STATUS_CONFIG = {
  PLANUNG: { label: 'Planung', color: 'bg-gray-100 text-gray-800' },
  IN_BEARBEITUNG: { label: 'In Bearbeitung', color: 'bg-blue-100 text-blue-800' },
  ABGESCHLOSSEN: { label: 'Abgeschlossen', color: 'bg-green-100 text-green-800' },
  ABGERECHNET: { label: 'Abgerechnet', color: 'bg-purple-100 text-purple-800' }
};

export default function ProjektDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [projekt, setProjekt] = useState(null);
  const [statistiken, setStatistiken] = useState(null);
  const [erweiterteStats, setErweiterteStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('uebersicht');
  const [showAddAnfrage, setShowAddAnfrage] = useState(false);
  const [verfuegbareAnfragen, setVerfuegbareAnfragen] = useState([]);
  const [showTerminForm, setShowTerminForm] = useState(false);
  const [terminLoading, setTerminLoading] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);

  // Animation States (wie Kundendetail)
  const [isPageReady, setIsPageReady] = useState(false);
  const [isContentVisible, setIsContentVisible] = useState(false);

  useEffect(() => {
    loadProjekt();
  }, [id]);

  const loadProjekt = async () => {
    try {
      const [projektData, statsData, erwStatsData] = await Promise.all([
        getProjektById(id),
        getProjektStatistiken(id),
        getErweiterteStatistiken(id).catch(() => null)
      ]);
      setProjekt(projektData);
      setStatistiken(statsData);
      setErweiterteStats(erwStatsData);

      // Animation starten (wie Kundendetail)
      requestAnimationFrame(() => {
        setIsPageReady(true);
        requestAnimationFrame(() => {
          setIsContentVisible(true);
        });
      });
    } catch (error) {
      toast.error('Projekt nicht gefunden');
      navigate('/projekte');
    } finally {
      setLoading(false);
    }
  };

  // Nur Statistiken neu laden (für Material/Zeiterfassung Updates)
  const reloadStats = async () => {
    try {
      const erwStatsData = await getErweiterteStatistiken(id);
      setErweiterteStats(erwStatsData);
    } catch (error) {
      console.error('Fehler beim Laden der Statistiken:', error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateProjekt(id, { status: newStatus });
      setProjekt({ ...projekt, status: newStatus });
      toast.success('Status aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Projekt wirklich löschen? Verknüpfungen werden aufgehoben.')) return;
    try {
      await deleteProjekt(id);
      toast.success('Projekt gelöscht');
      navigate('/projekte');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const loadVerfuegbareAnfragen = async () => {
    try {
      const anfragen = await getAllAnfragen({ kundeId: projekt.kundeId });
      // Nur Anfragen ohne Projekt oder diesem Projekt
      setVerfuegbareAnfragen(anfragen.filter(a => !a.projektId || a.projektId === id));
      setShowAddAnfrage(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Anfragen');
    }
  };

  const handleAddAnfrage = async (anfrageId) => {
    try {
      await addAnfrageToProjekt(id, anfrageId);
      toast.success('Anfrage hinzugefügt');
      loadProjekt();
      setShowAddAnfrage(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler');
    }
  };

  const handleRemoveAnfrage = async (anfrageId) => {
    try {
      await removeAnfrageFromProjekt(id, anfrageId);
      toast.success('Anfrage entfernt');
      loadProjekt();
    } catch (error) {
      toast.error('Fehler beim Entfernen');
    }
  };

  // Termin erstellen mit Projekt-Adresse
  const handleCreateTermin = async (terminData) => {
    setTerminLoading(true);
    try {
      // Termin erstellen
      const neuerTermin = await createTermin({
        ...terminData,
        projektId: id // Direkt mit Projekt verknüpfen
      });

      toast.success('Termin erstellt und verknüpft');
      setShowTerminForm(false);
      loadProjekt();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Erstellen');
    } finally {
      setTerminLoading(false);
    }
  };

  // Projekt-Adresse als Termin-Ort zusammenbauen
  const getProjektAdresse = () => {
    const parts = [];
    if (projekt?.strasse) parts.push(projekt.strasse);
    if (projekt?.plz || projekt?.ort) {
      parts.push([projekt.plz, projekt.ort].filter(Boolean).join(' '));
    }
    return parts.join(', ');
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!projekt) return null;

  return (
    <Layout>
      <motion.div
        className="container mx-auto px-4 sm:px-6 py-6 sm:py-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: isPageReady ? 1 : 0 }}
        transition={{ duration: 0.1 }}
      >
        {/* Header */}
        <motion.div
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: isPageReady ? 1 : 0 }}
          transition={{ duration: 0.15 }}
        >
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/projekte')} className="p-2 hover:bg-surface-elevated rounded-button transition">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-text-muted font-mono">{projekt.nummer}</span>
                <select
                  value={projekt.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className={`px-3 py-1 rounded-full text-sm font-medium border-0 cursor-pointer ${STATUS_CONFIG[projekt.status]?.color || 'bg-gray-100'}`}
                >
                  {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <h1 className="text-2xl sm:text-3xl font-serif font-bold text-text">{projekt.titel}</h1>
            </div>
          </div>
          <div className="flex gap-2 relative">
            <button
              onClick={() => setShowShareDialog(!showShareDialog)}
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-button hover:bg-surface-elevated transition"
              title="Öffentlichen Link teilen"
            >
              <Share2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate(`/projekte/${id}/bearbeiten`)}
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-button hover:bg-surface-elevated transition"
            >
              <Edit className="w-4 h-4" />
              Bearbeiten
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-button hover:bg-red-50 transition"
            >
              <Trash2 className="w-4 h-4" />
            </button>

            {/* Share Dialog */}
            {showShareDialog && (
              <div className="absolute right-0 top-12 z-50 w-80 bg-white rounded-xl shadow-xl border border-gray-200 p-4 space-y-3">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Link2 className="w-4 h-4" /> Öffentlicher Projekt-Link
                </h3>
                {projekt.publicToken ? (
                  <>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={`${window.location.origin}/public/projekt/${projekt.publicToken}`}
                        className="flex-1 text-xs px-3 py-2 border border-border rounded-lg bg-gray-50 truncate"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/public/projekt/${projekt.publicToken}`);
                          toast.success('Link kopiert!');
                        }}
                        className="p-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    {projekt.publicExpiresAt && (
                      <p className="text-xs text-gray-500">
                        Gültig bis: {new Date(projekt.publicExpiresAt).toLocaleDateString('de-DE')}
                      </p>
                    )}
                    <button
                      onClick={async () => {
                        try {
                          await revokePublicToken(id);
                          setProjekt(prev => ({ ...prev, publicToken: null, publicExpiresAt: null }));
                          toast.success('Link deaktiviert');
                        } catch (err) {
                          toast.error('Fehler beim Deaktivieren');
                        }
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                    >
                      <Unlink className="w-3.5 h-3.5" /> Link deaktivieren
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-gray-500">
                      Erstelle einen öffentlichen Link, über den Kunden den Projektfortschritt einsehen können (ohne Login).
                    </p>
                    <button
                      onClick={async () => {
                        try {
                          const result = await generatePublicToken(id);
                          setProjekt(prev => ({ ...prev, publicToken: result.publicToken, publicExpiresAt: result.publicExpiresAt }));
                          toast.success('Öffentlicher Link erstellt');
                        } catch (err) {
                          toast.error('Fehler beim Erstellen');
                        }
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 justify-center"
                    >
                      <Link2 className="w-3.5 h-3.5" /> Link generieren
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowShareDialog(false)}
                  className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <motion.div
            className="bg-surface rounded-card shadow-card p-4"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: isContentVisible ? 1 : 0, y: isContentVisible ? 0 : 15 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <div className="flex items-center gap-2 text-text-muted mb-1">
              <User className="w-4 h-4" />
              <span className="text-sm">Kunde</span>
            </div>
            <Link to={`/kunden/${projekt.kunde?.id}`} className="font-semibold text-text hover:text-primary transition">
              {projekt.kunde?.name}
            </Link>
          </motion.div>
          <motion.div
            className="bg-surface rounded-card shadow-card p-4"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: isContentVisible ? 1 : 0, y: isContentVisible ? 0 : 15 }}
            transition={{ duration: 0.2, ease: 'easeOut', delay: 0.03 }}
          >
            <div className="flex items-center gap-2 text-text-muted mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-sm">Zeitraum</span>
            </div>
            <p className="font-semibold text-text">
              {projekt.startDatum ? new Date(projekt.startDatum).toLocaleDateString('de-DE') : '-'}
              {projekt.endDatum && ` - ${new Date(projekt.endDatum).toLocaleDateString('de-DE')}`}
            </p>
          </motion.div>
          {/* Budget Card - mit visueller Warnung */}
          <motion.div className={`bg-surface rounded-card shadow-card p-4 ${
            erweiterteStats?.budget?.differenz < 0 ? 'ring-2 ring-red-400' : ''
          }`}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: isContentVisible ? 1 : 0, y: isContentVisible ? 0 : 15 }}
            transition={{ duration: 0.2, ease: 'easeOut', delay: 0.06 }}
          >
            <div className="flex items-center gap-2 text-text-muted mb-1">
              <Euro className="w-4 h-4" />
              <span className="text-sm">Budget</span>
              {erweiterteStats?.budget?.differenz < 0 && (
                <AlertTriangle className="w-4 h-4 text-red-500" />
              )}
            </div>
            {projekt.budgetNetto ? (
              <>
                <p className="font-semibold text-text">{formatCurrency(projekt.budgetNetto)}</p>
                {erweiterteStats?.budget && (
                  <div className="mt-1">
                    <div className="flex items-center gap-1 text-xs">
                      {erweiterteStats.budget.differenz >= 0 ? (
                        <TrendingUp className="w-3 h-3 text-green-500" />
                      ) : (
                        <TrendingDown className="w-3 h-3 text-red-500" />
                      )}
                      <span className={erweiterteStats.budget.differenz >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {erweiterteStats.budget.differenz >= 0 ? '+' : ''}{formatCurrency(erweiterteStats.budget.differenz)}
                      </span>
                    </div>
                    {/* Budget Progress Bar */}
                    <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          (erweiterteStats.budget.verbraucht / erweiterteStats.budget.budget) > 1
                            ? 'bg-red-500'
                            : (erweiterteStats.budget.verbraucht / erweiterteStats.budget.budget) > 0.8
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                        }`}
                        style={{
                          width: `${Math.min(100, (erweiterteStats.budget.verbraucht / erweiterteStats.budget.budget) * 100)}%`
                        }}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-text-muted text-sm">Nicht festgelegt</p>
            )}
          </motion.div>
          <motion.div
            className="bg-surface rounded-card shadow-card p-4"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: isContentVisible ? 1 : 0, y: isContentVisible ? 0 : 15 }}
            transition={{ duration: 0.2, ease: 'easeOut', delay: 0.09 }}
          >
            <div className="flex items-center gap-2 text-text-muted mb-1">
              <Receipt className="w-4 h-4" />
              <span className="text-sm">Rechnungen</span>
            </div>
            <p className="font-semibold text-text">{formatCurrency(statistiken?.rechnungGesamt || 0)}</p>
            <p className="text-xs text-text-muted">Offen: {formatCurrency(statistiken?.offen || 0)}</p>
          </motion.div>
          <motion.div
            className="bg-surface rounded-card shadow-card p-4"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: isContentVisible ? 1 : 0, y: isContentVisible ? 0 : 15 }}
            transition={{ duration: 0.2, ease: 'easeOut', delay: 0.12 }}
          >
            <div className="flex items-center gap-2 text-text-muted mb-1">
              <MapPin className="w-4 h-4" />
              <span className="text-sm">Adresse</span>
            </div>
            <p className="font-semibold text-text text-sm">
              {projekt.strasse || '-'}
              {(projekt.plz || projekt.ort) && <br />}
              {projekt.plz} {projekt.ort}
            </p>
          </motion.div>
        </div>

        {/* Tabs */}
        <motion.div
          className="border-b border-border mb-6 overflow-x-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: isContentVisible ? 1 : 0 }}
          transition={{ duration: 0.2, delay: 0.1 }}
        >
          <div className="flex gap-6 min-w-max">
            {[
              { id: 'uebersicht', label: 'Übersicht' },
              { id: 'anfragen', label: `Anfragen (${projekt.anfragen?.length || 0})` },
              { id: 'materialien', label: `Materialien (${erweiterteStats?.material?.anzahl || 0})`, icon: Package },
              { id: 'bestellungen', label: `Bestellungen (${erweiterteStats?.bestellungen?.anzahl || 0})`, icon: Truck },
              { id: 'zeiterfassung', label: `Zeiterfassung (${erweiterteStats?.arbeit?.eintraege || 0})`, icon: Clock },
              { id: 'meilensteine', label: `Meilensteine (${erweiterteStats?.meilensteine?.gesamt || 0})`, icon: Flag },
              { id: 'termine', label: `Termine (${projekt.termine?.length || 0})` },
              { id: 'kvas', label: `KVAs (${projekt.kostenvoranschlaege?.length || 0})` },
              { id: 'rechnungen', label: `Rechnungen (${projekt.rechnungen?.length || 0})` },
              { id: 'abrechnung', label: 'Abrechnung', icon: Zap }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 px-1 border-b-2 transition whitespace-nowrap flex items-center gap-1.5 ${
                  activeTab === tab.id
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-text-muted hover:text-text'
                }`}
              >
                {tab.icon && <tab.icon className="w-4 h-4" />}
                {tab.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Tab Content */}
        {activeTab === 'uebersicht' && (
          <div className="space-y-6">
            {/* Beschreibung und Notizen */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <motion.div
                className="bg-surface rounded-card shadow-card p-6"
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: isContentVisible ? 1 : 0, x: isContentVisible ? 0 : -15 }}
                transition={{ duration: 0.2, ease: 'easeOut', delay: 0.12 }}
              >
                <h3 className="font-semibold mb-4 text-text">Beschreibung</h3>
                <p className="text-text-muted whitespace-pre-wrap">{projekt.beschreibung || 'Keine Beschreibung'}</p>
              </motion.div>

              {/* Projekt-Chat (Notizen) */}
              <motion.div
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: isContentVisible ? 1 : 0, x: isContentVisible ? 0 : 15 }}
                transition={{ duration: 0.2, ease: 'easeOut', delay: 0.12 }}
              >
                <ProjektNotizen projektId={id} />
              </motion.div>
            </div>

            {/* Dateien (Fotos & Dokumente) */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: isContentVisible ? 1 : 0, y: isContentVisible ? 0 : 15 }}
              transition={{ duration: 0.2, ease: 'easeOut', delay: 0.15 }}
            >
              <ProjektDateien projektId={id} />
            </motion.div>

            {/* Timeline */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: isContentVisible ? 1 : 0, y: isContentVisible ? 0 : 15 }}
              transition={{ duration: 0.2, ease: 'easeOut', delay: 0.18 }}
            >
              <ProjektTimeline projektId={id} />
            </motion.div>
          </div>
        )}

        {activeTab === 'anfragen' && (
          <motion.div
            className="bg-surface rounded-card shadow-card p-6"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-text">Verknüpfte Anfragen</h3>
              <button
                onClick={loadVerfuegbareAnfragen}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-primary text-white rounded-button hover:bg-primary-dark transition"
              >
                <Plus className="w-4 h-4" />
                Anfrage hinzufügen
              </button>
            </div>
            {projekt.anfragen?.length === 0 ? (
              <p className="text-text-muted text-center py-8">Keine Anfragen verknüpft</p>
            ) : (
              <div className="space-y-3">
                {projekt.anfragen?.map((anfrage) => (
                  <div key={anfrage.id} className="flex justify-between items-center p-4 bg-surface-elevated rounded-button">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-text truncate">{anfrage.betreff}</p>
                      <p className="text-sm text-text-muted">
                        {new Date(anfrage.datum).toLocaleDateString('de-DE')} • {anfrage.kategorie} • {anfrage.status}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/kunden/${projekt.kundeId}?tab=anfragen&anfrage=${anfrage.id}`}
                        className="p-2 text-text-muted hover:text-primary transition"
                        title="Anfrage öffnen"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleRemoveAnfrage(anfrage.id)}
                        className="p-2 text-text-muted hover:text-red-500 transition"
                        title="Entfernen"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'termine' && (
          <motion.div
            className="bg-surface rounded-card shadow-card p-6"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-text">Termine</h3>
              <button
                onClick={() => setShowTerminForm(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-primary text-white rounded-button hover:bg-primary-dark transition"
              >
                <Plus className="w-4 h-4" />
                Termin erstellen
              </button>
            </div>
            {projekt.termine?.length === 0 ? (
              <p className="text-text-muted text-center py-8">Keine Termine verknüpft</p>
            ) : (
              <div className="space-y-3">
                {projekt.termine?.map((termin) => (
                  <div key={termin.id} className="flex justify-between items-center p-4 bg-surface-elevated rounded-button">
                    <div>
                      <p className="font-medium text-text">{termin.titel || 'Termin'}</p>
                      <p className="text-sm text-text-muted flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {new Date(termin.datum).toLocaleDateString('de-DE')} um {termin.uhrzeit} Uhr
                        {termin.ort && <span>• {termin.ort}</span>}
                      </p>
                    </div>
                    <Link
                      to={`/kalender?date=${termin.datum?.split('T')[0]}`}
                      className="p-2 text-text-muted hover:text-primary transition"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'kvas' && (
          <motion.div
            className="bg-surface rounded-card shadow-card p-6"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-text">Kostenvoranschläge</h3>
              {projekt.anfragen?.length > 0 ? (
                <Link
                  to={`/kostenvoranschlaege/neu?kundeId=${projekt.kundeId}&anfrageId=${projekt.anfragen[0]?.id}`}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-primary text-white rounded-button hover:bg-primary-dark transition"
                >
                  <Plus className="w-4 h-4" />
                  KVA erstellen
                </Link>
              ) : (
                <span className="text-xs text-text-muted bg-surface-elevated px-3 py-2 rounded-button">
                  Erst Anfrage verknüpfen
                </span>
              )}
            </div>

            {/* Budget-Hinweis wenn vorhanden */}
            {projekt.budgetNetto && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-button">
                <div className="flex items-center gap-2 text-sm text-blue-800">
                  <Euro className="w-4 h-4" />
                  <span>Geplantes Budget: <strong>{formatCurrency(projekt.budgetNetto)}</strong></span>
                </div>
                <p className="text-xs text-blue-600 mt-1">
                  Verwenden Sie diesen Betrag als Grundlage für Ihren Kostenvoranschlag
                </p>
              </div>
            )}

            {projekt.kostenvoranschlaege?.length === 0 ? (
              <p className="text-text-muted text-center py-8">Keine KVAs verknüpft</p>
            ) : (
              <div className="space-y-3">
                {projekt.kostenvoranschlaege?.map((kva) => (
                  <div key={kva.id} className="flex justify-between items-center p-4 bg-surface-elevated rounded-button">
                    <div>
                      <p className="font-medium text-text">{kva.nummer}</p>
                      <p className="text-sm text-text-muted">
                        {new Date(kva.datum).toLocaleDateString('de-DE')} • {kva.status}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="font-semibold text-text">{formatCurrency(kva.bruttogesamt)}</p>
                      <Link
                        to={`/kostenvoranschlaege?id=${kva.id}`}
                        className="p-2 text-text-muted hover:text-primary transition"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* KVA-Summen vs Budget Vergleich */}
            {projekt.kostenvoranschlaege?.length > 0 && projekt.budgetNetto && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-text-muted">KVA-Summe gesamt:</span>
                  <span className="font-medium">{formatCurrency(projekt.kostenvoranschlaege.reduce((sum, k) => sum + (k.nettogesamt || 0), 0))}</span>
                </div>
                <div className="flex justify-between items-center text-sm mt-1">
                  <span className="text-text-muted">Budget:</span>
                  <span className="font-medium">{formatCurrency(projekt.budgetNetto)}</span>
                </div>
                {(() => {
                  const kvaSumme = projekt.kostenvoranschlaege.reduce((sum, k) => sum + (k.nettogesamt || 0), 0);
                  const diff = projekt.budgetNetto - kvaSumme;
                  return (
                    <div className={`flex justify-between items-center text-sm mt-1 pt-1 border-t border-border ${diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      <span>Differenz:</span>
                      <span className="font-semibold">{diff >= 0 ? '+' : ''}{formatCurrency(diff)}</span>
                    </div>
                  );
                })()}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'rechnungen' && (
          <motion.div
            className="bg-surface rounded-card shadow-card p-6"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <h3 className="font-semibold mb-4 text-text">Rechnungen</h3>
            {projekt.rechnungen?.length === 0 ? (
              <p className="text-text-muted text-center py-8">Keine Rechnungen verknüpft</p>
            ) : (
              <div className="space-y-3">
                {projekt.rechnungen?.map((rechnung) => (
                  <div key={rechnung.id} className="flex justify-between items-center p-4 bg-surface-elevated rounded-button">
                    <div>
                      <p className="font-medium text-text">{rechnung.rechnungsnummer}</p>
                      <p className="text-sm text-text-muted">
                        {new Date(rechnung.datum).toLocaleDateString('de-DE')} • {rechnung.status}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="font-semibold text-text">{formatCurrency(rechnung.bruttogesamt)}</p>
                      <Link
                        to={`/rechnungen?id=${rechnung.id}`}
                        className="p-2 text-text-muted hover:text-primary transition"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Phase 2: Neue Tabs */}
        {activeTab === 'materialien' && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <ProjektMaterialien projektId={id} onDataChange={reloadStats} />
          </motion.div>
        )}

        {activeTab === 'bestellungen' && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <ProjektBestellungen projektId={id} />
          </motion.div>
        )}

        {activeTab === 'zeiterfassung' && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <ProjektZeiterfassung projektId={id} onDataChange={reloadStats} />
          </motion.div>
        )}

        {activeTab === 'meilensteine' && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <ProjektMeilensteine projektId={id} />
          </motion.div>
        )}

        {activeTab === 'abrechnung' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
            <ProjektGesamtrechnung
              projektId={id}
              projekt={projekt}
              statistiken={erweiterteStats}
              onRechnungCreated={loadProjekt}
            />
            </motion.div>
            {/* Projekt-Übersicht Card */}
            <motion.div
              className="bg-surface rounded-card shadow-card p-6"
              initial={{ opacity: 0, x: 15 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <h3 className="font-semibold mb-4 text-text">Projekt-Übersicht</h3>
              {erweiterteStats && (
                <div className="space-y-4">
                  {/* Meilensteine Fortschritt */}
                  <div>
                    <p className="text-sm text-text-muted mb-1">Meilensteine</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 transition-all duration-300"
                          style={{ width: `${erweiterteStats.meilensteine?.fortschritt || 0}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-text">
                        {erweiterteStats.meilensteine?.fortschritt || 0}%
                      </span>
                    </div>
                    <p className="text-xs text-text-muted mt-1">
                      {erweiterteStats.meilensteine?.erledigt || 0} von {erweiterteStats.meilensteine?.gesamt || 0} erledigt
                    </p>
                  </div>

                  {/* Statistiken Grid */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                    <div>
                      <p className="text-sm text-text-muted">Materialien</p>
                      <p className="font-semibold text-text">{erweiterteStats.material?.anzahl || 0} Positionen</p>
                      <p className="text-xs text-text-muted">{erweiterteStats.material?.verbaut || 0} verbaut</p>
                    </div>
                    <div>
                      <p className="text-sm text-text-muted">Arbeitsstunden</p>
                      <p className="font-semibold text-text">{(erweiterteStats.arbeit?.stunden || 0).toFixed(1)} Std.</p>
                      <p className="text-xs text-text-muted">{erweiterteStats.arbeit?.eintraege || 0} Einträge</p>
                    </div>
                    <div>
                      <p className="text-sm text-text-muted">Rechnungen</p>
                      <p className="font-semibold text-text">{formatCurrency(erweiterteStats.finanzen?.rechnungGesamt || 0)}</p>
                      <p className="text-xs text-text-muted">
                        {formatCurrency(erweiterteStats.finanzen?.offen || 0)} offen
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-text-muted">KVAs</p>
                      <p className="font-semibold text-text">{formatCurrency(erweiterteStats.finanzen?.kvaGesamt || 0)}</p>
                    </div>
                  </div>

                  {/* Budget wenn vorhanden */}
                  {erweiterteStats.budget && (
                    <div className={`p-3 rounded-button ${erweiterteStats.budget.differenz >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-text-muted">Budget</span>
                        <span className="font-medium text-text">{formatCurrency(erweiterteStats.budget.budget)}</span>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-sm text-text-muted">Verbraucht</span>
                        <span className="font-medium text-text">{formatCurrency(erweiterteStats.budget.verbraucht)}</span>
                      </div>
                      <div className="flex justify-between items-center mt-1 pt-1 border-t border-current border-opacity-10">
                        <span className="text-sm font-medium">Differenz</span>
                        <span className={`font-semibold ${erweiterteStats.budget.differenz >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {erweiterteStats.budget.differenz >= 0 ? '+' : ''}{formatCurrency(erweiterteStats.budget.differenz)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        )}

        {/* Add Anfrage Modal */}
        {showAddAnfrage && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-card p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-text">Anfrage hinzufügen</h3>
                <button onClick={() => setShowAddAnfrage(false)} className="p-2 hover:bg-surface-elevated rounded-button">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {verfuegbareAnfragen.filter(a => !a.projektId).length === 0 ? (
                <p className="text-text-muted text-center py-8">Keine verfügbaren Anfragen für diesen Kunden</p>
              ) : (
                <div className="space-y-2">
                  {verfuegbareAnfragen.filter(a => !a.projektId).map((anfrage) => (
                    <button
                      key={anfrage.id}
                      onClick={() => handleAddAnfrage(anfrage.id)}
                      className="w-full text-left p-4 border border-border rounded-button hover:bg-surface-elevated transition"
                    >
                      <p className="font-medium text-text">{anfrage.betreff}</p>
                      <p className="text-sm text-text-muted">
                        {new Date(anfrage.datum).toLocaleDateString('de-DE')} • {anfrage.kategorie}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Termin erstellen Modal */}
        {showTerminForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-text">Neuer Termin für Projekt</h3>
                <button onClick={() => setShowTerminForm(false)} className="p-2 hover:bg-surface-elevated rounded-button">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-4 p-3 bg-surface-elevated rounded-button">
                <p className="text-sm text-text-muted">
                  <strong>Projekt:</strong> {projekt.titel}
                </p>
                {getProjektAdresse() && (
                  <p className="text-sm text-text-muted mt-1">
                    <strong>Adresse:</strong> {getProjektAdresse()}
                  </p>
                )}
              </div>
              <TerminForm
                termin={{
                  ort: getProjektAdresse() // Projekt-Adresse als Ort vorausfüllen
                }}
                preselectedKunde={projekt.kunde}
                onSubmit={handleCreateTermin}
                onCancel={() => setShowTerminForm(false)}
                isLoading={terminLoading}
              />
            </div>
          </div>
        )}
      </motion.div>
    </Layout>
  );
}
